import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { currentPostAtom } from "../../../atom";

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Modal, Form, Select, message, Dropdown, Button } from "antd";
import { EditOutlined, SwapOutlined, SaveOutlined, CloseOutlined, PlusOutlined, UpOutlined } from "@ant-design/icons";

import styled, { css } from "styled-components";
import MainCategoryMenu from "./MainCategoryMenu";

import { getLoginUser } from "../../../api/user";
import { newGetCategory, getBoardListWithRootCategory } from "../../../api/categoryAPI";
import { createPost, getPostList, changeCategory, getPostData, reorderPosts, addConference } from "../../../api/postAPI";

interface SortableItemProps {
  post: any;
  isOrderEditing: boolean;
  selectedPostId: string;
  isMultiSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
  isAdmin: boolean;
  onContextMenu: (post: any) => void;
  onAddConference: (post: any) => void;
}

const SortableItem = ({ post, isOrderEditing, selectedPostId, isMultiSelected, onClick, isAdmin, onContextMenu, onAddConference }: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: post.id, disabled: !isOrderEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto",
    touchAction: "none" as React.CSSProperties["touchAction"],
  };

  if (isOrderEditing) {
    return (
      <StPostItem
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        $isSelected={selectedPostId === String(post.id)}
        $isMultiSelected={false}
        $isEditing={true}
        $isOrderMode={true}
      >
        {post.title || post.name || "제목 없음"}
      </StPostItem>
    );
  }

  if (isAdmin) {
    return (
      <Dropdown
        trigger={['contextMenu']}
        menu={{ items: [
          { key: 'edit', label: '카테고리 편집', icon: <EditOutlined />, onClick: () => onContextMenu(post) },
          { key: '', label: '학술강좌 등록', icon: <UpOutlined />, onClick: () => onAddConference(post) },
        ]}}
      >
        <StPostItem
          $isSelected={selectedPostId === String(post.id)}
          $isMultiSelected={isMultiSelected}
          onClick={onClick}
          $isEditing={false}
          $isOrderMode={false}
        >
          {post.title || post.name || "제목 없음"}
        </StPostItem>
      </Dropdown>
    );
  }

  return (
    <StPostItem
      $isSelected={selectedPostId === String(post.id)}
      $isMultiSelected={false}
      onClick={onClick}
      $isEditing={false}
      $isOrderMode={false}
    >
      {post.title || post.name || "제목 없음"}
    </StPostItem>
  );
};

interface Props {
  rootCategoryId: number;
}

const GeneralTypeItem = ({ rootCategoryId }: Props) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [ mainCategoryList, setMainCategoryList ] = useState<any[]>([]);
  const [ subCategoryList, setSubCategoryList ] = useState<any[]>([]);
  
  const [ activeMainCategory, setActiveMainCategory ] = useState<string>(searchParams.get("main") || "");
  const [ activeSubCategory, setActiveSubCategory ] = useState<string>(searchParams.get("sub") || "");
  
  const [ postList, setPostList ] = useState<any[]>([]);
  const [ selectedPostId, setSelectedPostId ] = useState<string>(searchParams.get("post") || "");
  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);

  const [multiSelectedPostIds, setMultiSelectedPostIds] = useState<string[]>([]);
  const [editingPosts, setEditingPosts] = useState<any[]>([]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [boardList, setBoardList] = useState<any[]>([]);
  const [editModalMains, setEditModalMains] = useState<any[]>([]);
  const [editModalSubs, setEditModalSubs] = useState<any[]>([]);
  const [isNoSubCategory, setIsNoSubCategory] = useState(false);
  const [form] = Form.useForm();

  const [isConferenceModalOpen, setIsConferenceModalOpen] = useState(false);
  const [conferencePosts, setConferencePosts] = useState<any[]>([]);
  const [conferenceBoardList, setConferenceBoardList] = useState<any[]>([]);
  const [conferenceMainList, setConferenceMainList] = useState<any[]>([]);
  const [conferenceSubList, setConferenceSubList] = useState<any[]>([]);
  const [conferenceForm] = Form.useForm();

  const [isOrderEditing, setIsOrderEditing] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const updateParams = ( main: string, sub: string, post?: string, mainName?: string, subName?: string ) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("main", main);
    newParams.set("sub", sub);
    if (post) newParams.set("post", post);
    else newParams.delete("post");
    if (mainName) newParams.set("mainName", mainName);
    if (subName) newParams.set("subName", subName);
    setSearchParams(newParams, { replace: true });
  }

  const handleMainCategoryChange = (category: string) => {
    setActiveMainCategory(category);
    setPostList([]);
    setActiveSubCategory("");
    setSelectedPostId("");
    setMultiSelectedPostIds([]); 
    setIsOrderEditing(false);

    const mainCategory = mainCategoryList.find(cat => String(cat.id) === category);
    if (mainCategory) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("main", category);
      newParams.set("mainName", mainCategory.name);
      newParams.delete("sub");
      newParams.delete("subName");
      newParams.delete("post");

      setSearchParams(newParams, { replace: true });
    }
  };

  const handleSubCategoryChange = (category: string) => {
    setActiveSubCategory(category);
    setSelectedPostId("");
    setMultiSelectedPostIds([]); 
    setIsOrderEditing(false);

    const subCategory = subCategoryList.find(cat => String(cat.id) === category);
    const mainCategory = mainCategoryList.find(cat => String(cat.id) === activeMainCategory);
    updateParams(
      activeMainCategory,
      category,
      undefined,
      mainCategory?.name,
      subCategory?.name
    );
  };

  const handlePostClick = async (e: React.MouseEvent, post: any) => {
    if (isOrderEditing) return;

    if (isAdmin && (e.ctrlKey || e.metaKey)) {
      const postIdStr = String(post.id);
      setMultiSelectedPostIds((prev) => {
        if (prev.includes(postIdStr)) {
          return prev.filter((id) => id !== postIdStr);
        } else {
          return [...prev, postIdStr];
        }
      });
      return; 
    }

    setMultiSelectedPostIds([]);

    const postIdStr = String(post.id);
    setSelectedPostId(postIdStr);

    const mainCategory = mainCategoryList.find(cat => String(cat.id) === activeMainCategory);
    const subCategory = subCategoryList.find(cat => String(cat.id) === activeSubCategory);
    updateParams(
      activeMainCategory,
      activeSubCategory,
      postIdStr,
      mainCategory?.name,
      subCategory?.name
    );

    const data = await getPostData(post.id);
    setCurrentPost(data);

    const params = new URLSearchParams();
    params.set('from', 'admin');
    params.set('title', post.title || post.name || '제목 없음');

    const board = searchParams.get('board');
    const boardName = searchParams.get('boardName');
    if (board) params.set('board', board);
    if (boardName) params.set('boardName', boardName);
    if (activeMainCategory) params.set('main', activeMainCategory);
    if (mainCategory) params.set('mainName', mainCategory.name);
    if (activeSubCategory) params.set('sub', activeSubCategory);
    if (subCategory) params.set('subName', subCategory.name);

    navigate(`/post/${post.id}?${params.toString()}`);
  };

  const handleAddPost = async () => {
    if (isOrderEditing) return message.warning("순서 변경을 먼저 완료해주세요.");
    const postId = await createPost(Number(activeSubCategory));
    setCurrentPost({
      id: postId,
      categoryId: Number(activeSubCategory),
      title: "",
      writer: "",
      slides: [],
      isActive: false,
      createdAt: "",
      modifiedAt: "",
      content: "",
      postThumbnail: "",
      comments: [],
      isSecret: false,
    });
    navigate("/write");
  };

  const handleContextMenu = (post: any) => {
    const postIdStr = String(post.id);
    
    if (multiSelectedPostIds.includes(postIdStr)) {
      const selectedPostsData = postList.filter(p => multiSelectedPostIds.includes(String(p.id)));
      openEditModal(selectedPostsData);
    } else {
      setMultiSelectedPostIds([]);
      openEditModal([post]);
    }
  };

  const handleConferenceBoardChange = async (boardId: number, boards = conferenceBoardList) => {
    const board = boards.find((b: any) => b.board_id === boardId);
    const rootId = board?.rootCategories?.[0]?.id;

    setConferenceMainList([]);
    setConferenceSubList([]);
    conferenceForm.setFieldsValue({ mainId: undefined, subId: undefined });

    if (rootId) {
      const data = await newGetCategory(rootId);
      if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
      setConferenceMainList(data || []);
    }
  };

  const openConferenceModal = async (posts: any[]) => {
    setConferencePosts(posts);
    conferenceForm.resetFields();
    setConferenceMainList([]);
    setConferenceSubList([]);

    const conferenceBoards = boardList.filter((b: any) => b.name === '학술강좌');
    setConferenceBoardList(conferenceBoards);

    if (conferenceBoards.length === 1) {
      const board = conferenceBoards[0];
      conferenceForm.setFieldsValue({ boardId: board.board_id });
      await handleConferenceBoardChange(board.board_id, conferenceBoards);
    }

    setIsConferenceModalOpen(true);
  };

  const handleConferenceMainChange = async (mainId: number) => {
    setConferenceSubList([]);
    conferenceForm.setFieldsValue({ subId: undefined });

    const data = await newGetCategory(mainId);
    if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
    setConferenceSubList(data || []);
  };

  const handleAddConference = (post: any) => {
    const postIdStr = String(post.id);
    const targets = multiSelectedPostIds.includes(postIdStr)
      ? postList.filter(p => multiSelectedPostIds.includes(String(p.id)))
      : [post];
    openConferenceModal(targets);
  };

  const onConferenceSubmit = async (values: any) => {
    try {
      const categoryId: number = values.subId ?? values.mainId;

      const results = await Promise.all(
        conferencePosts.map(post => addConference(post.id, categoryId))
      );

      const failed = results.filter(r => !r.success);
      if (failed.length > 0) {
        message.error(`${failed.length}개 등록 실패: ${failed[0].message}`);
      } else {
        message.success(`${conferencePosts.length}개의 포스트가 학술강좌에 등록되었습니다.`);
      }

      setIsConferenceModalOpen(false);
      setConferencePosts([]);
      setMultiSelectedPostIds([]);
    } catch (error) {
      console.error(error);
      message.error("학술강좌 등록 중 오류가 발생했습니다.");
    }
  };

  const openEditModal = (posts: any[]) => {
    setEditingPosts(posts);

    const currentBoard = boardList.find(b => b.rootCategories?.[0]?.id === rootCategoryId);
    const noSub = currentBoard?.categoryType === 'PATHOLOGY_PRACTICE';
    
    setIsNoSubCategory(noSub);
    setEditModalMains(mainCategoryList); 
    
    form.setFieldsValue({ 
      boardId: currentBoard?.board_id || undefined,
      mainId: Number(activeMainCategory), 
      subId: noSub ? undefined : Number(activeSubCategory) 
    });
    
    handleModalMainChange(Number(activeMainCategory), noSub);
    setIsEditModalOpen(true);
  };

  const handleModalBoardChange = async (boardId: number) => {
    const selectedBoard = boardList.find(b => b.board_id === boardId);
    const rootId = selectedBoard?.rootCategories?.[0]?.id;
    const noSub = selectedBoard?.categoryType === 'PATHOLOGY_PRACTICE';

    setIsNoSubCategory(noSub);

    if (rootId) {
      const data = await newGetCategory(rootId);
      if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
      setEditModalMains(data || []);
    } else {
      setEditModalMains([]);
    }
    
    setEditModalSubs([]);
    form.setFieldsValue({ mainId: undefined, subId: undefined });
  };

  const handleModalMainChange = async (mainId: number, skipSub = isNoSubCategory) => {
    if (skipSub) {
      setEditModalSubs([]);
      form.setFieldsValue({ subId: undefined });
      return;
    }

    const data = await newGetCategory(mainId);
    if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
    setEditModalSubs(data || []);
    form.setFieldsValue({ subId: undefined });
  };

  const onEditSubmit = async (values: any) => {
    try {
      if (editingPosts.length === 0) return;

      const targetCategoryId = isNoSubCategory ? values.mainId : values.subId;

      const data = editingPosts.map((post, index) => ({ 
        postId: post.id, 
        categoryId: targetCategoryId, 
        listOrder: index + 1 
      }));
      
      await changeCategory(data);

      message.success(`${editingPosts.length}개의 포스트가 변경되었습니다.`);
      setIsEditModalOpen(false);
      setMultiSelectedPostIds([]);
      setEditingPosts([]);
      fetchPostData(); 
    } catch (error) {
      console.error(error);
      message.error("변경 중 오류가 발생했습니다.");
    }
  };

  const getCategoryList = async () => {
    const data = await newGetCategory(rootCategoryId);
    if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
    setMainCategoryList(data || []);
    if(!searchParams.get("main") && data?.length) {
       setActiveMainCategory(String(data[0].id));
       updateParams(String(data[0].id), "0", undefined, data[0].name);
    }
  };

  const getSubCategoryList = async () => {
    const data = await newGetCategory(Number(activeMainCategory));
    if (data?.length) data.sort((a: any, b: any) => a.listOrder - b.listOrder);
    setSubCategoryList(data || []);
    const subParam = searchParams.get("sub");
    if (data?.length) {
      if (subParam && data.some((item: any) => String(item.id) === subParam)) {
        setActiveSubCategory(subParam);
      } else if (!subParam || activeSubCategory === "") {
        setActiveSubCategory(String(data[0].id));
        const mainCategory = mainCategoryList.find(cat => String(cat.id) === activeMainCategory);
        updateParams(activeMainCategory, String(data[0].id), undefined, mainCategory?.name, data[0].name);
      }
    }
  };

  const fetchPostData = async () => {
    try {
      setPostList([]);
      const postListData = await getPostList(1, 9999, Number(activeSubCategory), "id", "DESC");
      if (postListData?.length) postListData.sort((a: any, b: any) => a.listOrder - b.listOrder);
      
      setPostList(postListData);
    } catch (error) { console.error(error); }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPostList((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = async () => {
    const reorderData = postList.map((post, index) => ({ postId: post.id, newOrder: index + 1 }));
    await reorderPosts(reorderData);
    message.success("순서가 저장되었습니다.");
    setIsOrderEditing(false);
  };

  const handleCancelOrder = () => {
    setIsOrderEditing(false);
    fetchPostData(); 
  };

  useEffect(() => {
    getLoginUser().then(u => setIsAdmin(u?.role === 'ADMIN_MAIN')).catch(() => setIsAdmin(false));
    getCategoryList();
    
    getBoardListWithRootCategory().then(data => {
      if (data) {
        const filteredBoards = data.filter((b: any) => b.categoryType !== 'CONFERENCE');
        setBoardList(filteredBoards);
      }
    });
  }, []);

  useEffect(() => {
     if(activeMainCategory) getSubCategoryList();
  }, [activeMainCategory]);

  useEffect(() => {
    if(activeSubCategory && activeSubCategory !== "0") fetchPostData();
  }, [activeSubCategory]);

  return(
    <StContainer>
      <StMenuContainer>
       <MainCategoryMenu
         rootCategoryId={rootCategoryId}
         categoryType="GENERAL"
         categories={mainCategoryList} 
         activeCategory={activeMainCategory} 
         onCategoryChange={handleMainCategoryChange}
         reloadCategory={getCategoryList}
       />
        <MainCategoryMenu
          rootCategoryId={Number(activeMainCategory)}
          categoryType="GENERAL"
          categories={subCategoryList} 
          activeCategory={activeSubCategory} 
          parentCategoryList={mainCategoryList}
          onCategoryChange={handleSubCategoryChange}
          reloadCategory={getSubCategoryList}
        />
      </StMenuContainer>
      
      <div style={{ flex: 1 }}>
        <DndContext 
          sensors={sensors} 
          collisionDetection={closestCenter} 
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={postList.map(p => p.id)} 
            strategy={verticalListSortingStrategy}
          >
            <StPostList $isOrderEditing={isOrderEditing}>
              {postList.map((post) => (
                <SortableItem
                  key={post.id}
                  post={post}
                  isOrderEditing={isOrderEditing}
                  selectedPostId={selectedPostId}
                  isMultiSelected={multiSelectedPostIds.includes(String(post.id))} 
                  onClick={(e) => handlePostClick(e, post)} 
                  isAdmin={isAdmin}
                  onContextMenu={handleContextMenu}
                  onAddConference={handleAddConference}
                />
              ))}

              {!isOrderEditing ? (
                <>
                  <StAddPostButton onClick={handleAddPost} $isSelected={false} $isMultiSelected={false} $isOrderMode={false}>
                      <PlusOutlined /> Add Post
                  </StAddPostButton>
                  
                  {isAdmin && postList.length > 1 && (
                    <StControlBox $isSelected={false} $isOrderMode={false}>
                      <Button icon={<SwapOutlined />} onClick={() => setIsOrderEditing(true)}>순서 변경</Button>
                    </StControlBox>
                  )}
                </>
              ) : (
                <StControlBox style={{ width: "100%", marginTop: 10 }} $isSelected={false} $isOrderMode={true}>
                   <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveOrder} style={{marginRight: 8}}>저장</Button>
                   <Button icon={<CloseOutlined />} onClick={handleCancelOrder} danger>취소</Button>
                </StControlBox>
              )}
            </StPostList>
          </SortableContext>
        </DndContext>
      </div>

      <Modal
        title={`포스트 카테고리 변경 (${editingPosts.length}개)`}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onOk={() => form.submit()}
        centered
      >
        <Form form={form} layout="vertical" onFinish={onEditSubmit}>
          <Form.Item 
            name="boardId" 
            label="게시판" 
            rules={[{ required: true, message: '게시판을 선택해주세요' }]}
          >
            <Select 
              options={boardList.map(b => ({ label: b.name, value: b.board_id }))} 
              onChange={handleModalBoardChange}
              placeholder="게시판을 선택하세요"
            />
          </Form.Item>
          
          <Form.Item 
            name="mainId" 
            label="메인 카테고리"
            rules={[{ required: true, message: '메인 카테고리를 선택해주세요' }]}
          >
            <Select 
              options={editModalMains.map(c => ({ label: c.name, value: c.id }))} 
              onChange={(val) => handleModalMainChange(val)}
              placeholder="메인 카테고리를 선택하세요"
            />
          </Form.Item>

          {!isNoSubCategory && (
            <Form.Item 
              name="subId" 
              label="서브 카테고리"
              rules={[{ required: true, message: '서브 카테고리를 선택해주세요' }]}
            >
              <Select 
                options={editModalSubs.map(c => ({ label: c.name, value: c.id }))} 
                placeholder="서브 카테고리를 선택하세요"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={`학술강좌 등록 (${conferencePosts.length}개)`}
        open={isConferenceModalOpen}
        onCancel={() => setIsConferenceModalOpen(false)}
        onOk={() => conferenceForm.submit()}
        centered
      >
        <Form form={conferenceForm} layout="vertical" onFinish={onConferenceSubmit}>
          {conferenceBoardList.length > 1 && (
            <Form.Item
              name="boardId"
              label="학술강좌 게시판"
              rules={[{ required: true, message: '게시판을 선택해주세요' }]}
            >
              <Select
                options={conferenceBoardList.map(b => ({ label: b.name, value: b.board_id }))}
                onChange={(val) => handleConferenceBoardChange(val)}
                placeholder="게시판을 선택하세요"
              />
            </Form.Item>
          )}

          <Form.Item
            name="mainId"
            label="메인 카테고리"
            rules={[{ required: true, message: '메인 카테고리를 선택해주세요' }]}
          >
            <Select
              options={conferenceMainList.map(c => ({ label: c.name, value: c.id }))}
              onChange={handleConferenceMainChange}
              placeholder="메인 카테고리를 선택하세요"
            />
          </Form.Item>

          {conferenceSubList.length > 0 && (
            <Form.Item
              name="subId"
              label="서브 카테고리"
              rules={[{ required: true, message: '서브 카테고리를 선택해주세요' }]}
            >
              <Select
                options={conferenceSubList.map(c => ({ label: c.name, value: c.id }))}
                placeholder="서브 카테고리를 선택하세요"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </StContainer>
  );
};

const StContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  align-items: flex-start;
`;

const StMenuContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-shrink: 0;
`;

const StPostList = styled.div<{ $isOrderEditing: boolean }>`
  display: flex;
  gap: 10px;
  width: 100%;
  
  ${props => props.$isOrderEditing ? css`
    flex-direction: column; 
    border: 2px dashed #1890ff;
    padding: 10px;
    border-radius: 8px;
    background-color: #f0f5ff;
  ` : css`
    flex-direction: column;
  `}
`;

const StPostItem = styled.div<{ $isSelected: boolean, $isMultiSelected?: boolean, $isEditing?: boolean, $isOrderMode?: boolean }>`
  transition: background-color 0.2s, border-color 0.2s; 

  ${props => props.$isOrderMode ? css`
    width: 100%;
    padding: 12px 15px;
    
    border: 1px solid ${props.$isSelected ? '#4285f4' : '#d9d9d9'};
    border-radius: 4px;
    background-color: ${props.$isSelected ? '#e6f7ff' : '#fff'};
    
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    
    cursor: grab;
    &:active { cursor: grabbing; }

    &:hover {
      background-color: ${props.$isSelected ? '#bae7ff' : '#fafafa'};
      border-color: #40a9ff;
    }
  ` : css`
    width: 100%;
    padding: 12px 15px;
    
    border-bottom: 1px solid #f0f0f0;
    
    background-color: ${
      props.$isMultiSelected 
        ? '#d6e4ff' /* 다중 선택 시 진한 파랑 */ 
        : props.$isSelected 
          ? '#e6f7ff' /* 조회 중일 때 연한 파랑 */
          : 'transparent'
    };

    border-left: ${props.$isMultiSelected ? '4px solid #1890ff' : 'none'};

    border-radius: 4px;
    
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    
    cursor: pointer;
    font-size: 14px;
    color: #333;

    &:hover {
      background-color: ${props.$isMultiSelected ? '#d6e4ff' : (props.$isSelected ? '#d6efff' : '#fafafa')};
    }
    
    &:last-child {
      border-bottom: none;
    }
  `}
`;

const StAddPostButton = styled(StPostItem)`
  ${props => props.$isOrderMode ? css`
    border: 2px dashed #ccc;
    background-color: #fafafa;
    color: #666;
    font-weight: bold;
    justify-content: center;
    &:hover { background-color: #f0f0f0; border-color: #999; color: #333; }
  ` : css`
    border: 1px dashed #d9d9d9;
    border-radius: 4px;
    margin-top: 5px;
    justify-content: center;
    color: #888;
    background-color: #fff;
    &:hover { border-color: #40a9ff; color: #40a9ff; background-color: #fff; }
  `}
`;

const StControlBox = styled.div<{ $isSelected: boolean, $isOrderMode?: boolean }>`
  display: flex;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  
  justify-content: ${props => props.$isOrderMode ? 'flex-end' : 'flex-end'};
`;

export default GeneralTypeItem;